import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import AllOffersTable from "../Tables/Table/AllOffersTable";
import { getPartOffer } from "../../reduxStore/entities/partOffers";
import Breadcrumb from "../../../SharedComponents/components/Breadcrumb/Breadcrumb";
import ArrowDownSVG from "../../assets/icons/svg/ArrowDownSVG";
import ArrowUpSVG from "../../assets/icons/svg/ArrowUpSVG";
import { getFallbackMakeLogoImage } from "../../utils/carLogo/getFallbackMakeLogoImage";
import FilterOffersComponent from "./FilterOffersComponent";
import OfferDetails from "./OfferDetails";
import SearchOffersComponent from "./SearchOffersComponent";

const OfferPageComponent = () => {
    const dispatch = useDispatch();

    const [expandedBrands, setExpandedBrands] = useState([]);
    const [expandedModels, setExpandedModels] = useState({});
    const [selectedCars, setSelectedCars] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedOfferId, setSelectedOfferId] = useState(null);
    const [paginationByModel, setPaginationByModel] = useState({});

    const fetchedPartOffers = useSelector(
        (state) => state.partOffers.partOffer?.rows || []
    );
    const partOfferCount = useSelector(
        (state) => state.partOffers.partOffer?.count || 0
    );

    useEffect(() => {
        async function fetchOffers() {
            await dispatch(getPartOffer({ limit: 500, offset: 0 }));
        }
        fetchOffers();
    }, [dispatch]);

    const filteredOffers = fetchedPartOffers
        .filter((offer) =>
            offer.description?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .filter((offer) =>
            selectedCars.length === 0
                ? true
                : selectedCars.some(
                    (selected) =>
                        selected.value === offer.PartRequest.id_car_generation
                )
        );

    const offersGrouped = filteredOffers.reduce((acc, offer) => {
        const makeName = offer?.PartRequest?.Make?.name || "Nepoznata marka";
        const modelName = offer?.PartRequest?.Model?.name || "Nepoznat model";

        if (!acc[makeName]) acc[makeName] = {};
        if (!acc[makeName][modelName]) acc[makeName][modelName] = [];

        acc[makeName][modelName].push(offer);
        return acc;
    }, {});

    const toggleBrand = (brand) => {
        setExpandedBrands((prev) =>
            prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
        );
    };

    const toggleModel = (brand, model) => {
        setExpandedModels((prev) => {
            const current = prev[brand] || [];
            return {
                ...prev,
                [brand]: current.includes(model)
                    ? current.filter((m) => m !== model)
                    : [...current, model],
            };
        });
    };

    const getPagination = (brand, model) => {
        const key = `${brand}_${model}`;
        return paginationByModel[key] || { page: 0, rowsPerPage: 15 };
    };

    const handleChangePage = (brand, model, newPage) => {
        const key = `${brand}_${model}`;
        setPaginationByModel((prev) => ({
            ...prev,
            [key]: {
                ...getPagination(brand, model),
                page: newPage,
            },
        }));
    };

    const handleRowsPerPageChangeAdapter = (brand, model) => (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);

        const key = `${brand}_${model}`;
        setPaginationByModel((prev) => ({
            ...prev,
            [key]: {
                page: 0,
                rowsPerPage: newRowsPerPage,
            },
        }));
    };



    const handlePageChangeAdapter = (brand, model) => (event, newPage) => {
        handleChangePage(brand, model, newPage);
    };

    const breadcrumbs = [
        { title: "Početak", href: "/home-page" },
        { title: "Ponude", href: "#" },
    ];

    if (selectedOfferId !== null) {
        return <OfferDetails id={selectedOfferId} onBack={() => setSelectedOfferId(null)} />;
    }

    return (
        <div style={styles.wrapper}>
            <div style={styles.breadcrumbWrapper}>
                <Breadcrumb title="Ponude" breadcrumbs={breadcrumbs} />
            </div>

            <div style={styles.filterWrapper}>
                <FilterOffersComponent
                    offers={fetchedPartOffers}
                    selectedCars={selectedCars}
                    setSelectedCars={setSelectedCars}
                />
                <SearchOffersComponent searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>

            <div style={styles.groupedWrapper}>
                {Object.entries(offersGrouped).map(([brandName, models]) => (
                    <div key={brandName}>
                        <div onClick={() => toggleBrand(brandName)} style={styles.brandCard}>
                            <div style={styles.brandInfo}>
                                <img
                                    src={getFallbackMakeLogoImage(brandName)}
                                    alt={brandName}
                                    style={styles.brandImage}
                                />
                                <div style={styles.brandText}>
                                    <span style={styles.brandName}>{brandName}</span>
                                    <p style={styles.brandSubtitle}>Svi modeli</p>
                                </div>
                            </div>
                            <span>{expandedBrands.includes(brandName) ? <ArrowUpSVG /> : <ArrowDownSVG />}</span>
                        </div>

                        {expandedBrands.includes(brandName) && (
                            <div style={styles.modelListWrapper}>
                                {Object.entries(models).map(([modelName, modelOffers]) => {
                                    const { page, rowsPerPage } = getPagination(brandName, modelName);


                                    const paginatedModelOffers = modelOffers.slice(
                                        page * rowsPerPage,
                                        page * rowsPerPage + rowsPerPage
                                    );

                                    return (
                                        <div key={modelName} style={styles.modelCard}>
                                            <div
                                                onClick={() => toggleModel(brandName, modelName)}
                                                style={styles.modelHeader}
                                            >
                                                <span style={styles.modelName}>{modelName}</span>
                                                <span>
                                                    {expandedModels[brandName]?.includes(modelName) ? (
                                                        <ArrowUpSVG />
                                                    ) : (
                                                        <ArrowDownSVG />
                                                    )}
                                                </span>
                                            </div>

                                            {expandedModels[brandName]?.includes(modelName) && (
                                                <div style={styles.tableWrapper}>
                                                    <AllOffersTable
                                                        count={modelOffers.length}
                                                        fetchedActiveCarOffers={paginatedModelOffers}
                                                        rowsPerPage={rowsPerPage}
                                                        page={page}
                                                        onRowSelect={(id) => setSelectedOfferId(id)}
                                                        onPageChange={handlePageChangeAdapter(brandName, modelName)}
                                                        onRowsPerPageChange={handleRowsPerPageChangeAdapter(brandName, modelName)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OfferPageComponent;

const styles = {
    wrapper: {
        padding: "20px",
    },
    breadcrumbWrapper: {
        marginBottom: "30px",
        marginTop: "20px",
    },
    filterWrapper: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        marginBottom: "20px",
    },
    groupedWrapper: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
    brandCard: {
        cursor: "pointer",
        backgroundColor: "#f2f2f2",
        border: "1px solid #ccc",
        borderRadius: "6px",
        padding: "10px 15px",
        marginBottom: "5px",
        fontWeight: "bold",
        fontSize: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        width: "1137px",
        height: "80px",
    },
    brandInfo: {
        display: "flex",
        alignItems: "center",
    },
    brandImage: {
        width: "80px",
        height: "80px",
        objectFit: "contain",
        marginRight: 12,
    },
    brandText: {
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
    },
    brandName: {
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: 20,
        fontWeight: 600,
        fontStyle: "normal",
        letterSpacing: 0.15,
    },
    brandSubtitle: {
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: 16,
        fontWeight: 400,
    },
    modelListWrapper: {
        marginTop: "30px",
        paddingLeft: "50px",
        borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    },
    modelCard: {
        marginBottom: "10px",
        borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
    },
    modelHeader: {
        cursor: "pointer",
        backgroundColor: "#f2f2f2",
        border: "1px solid #ddd",
        borderRadius: "4px",
        padding: "8px 12px",
        marginBottom: "5px",
        fontSize: "15px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "1090px",
        height: "48px",
    },
    modelName: {
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: 20,
        fontWeight: 600,
        fontStyle: "normal",
        letterSpacing: 0.15,
    },
    tableWrapper: {
        marginTop: "10px",
    },
};
